import { Injectable, computed, signal } from '@angular/core';
import { DateRangeParams } from '../models/common.models';

type DraftRange = {
  from: string;
  to: string;
};

@Injectable({ providedIn: 'root' })
export class DateRangeService {
  readonly draftFrom = signal(this.formatDateInput(this.daysAgo(29)));
  readonly draftTo = signal(this.formatDateInput(new Date()));
  readonly appliedRevision = signal(0);
  readonly appliedRange = signal<DraftRange>({
    from: this.formatDateInput(this.daysAgo(29)),
    to: this.formatDateInput(new Date()),
  });

  readonly label = computed(() => `${this.appliedRange().from} to ${this.appliedRange().to}`);

  updateDraftFrom(value: string): void {
    this.draftFrom.set(value);
  }

  updateDraftTo(value: string): void {
    this.draftTo.set(value);
  }

  apply(): void {
    const normalized = this.normalizeRange(this.draftFrom(), this.draftTo());
    this.draftFrom.set(normalized.from);
    this.draftTo.set(normalized.to);
    this.appliedRange.set(normalized);
    this.appliedRevision.update((value) => value + 1);
  }

  resetLast30Days(): void {
    const next = {
      from: this.formatDateInput(this.daysAgo(29)),
      to: this.formatDateInput(new Date()),
    };

    this.draftFrom.set(next.from);
    this.draftTo.set(next.to);
    this.appliedRange.set(next);
    this.appliedRevision.update((value) => value + 1);
  }

  toQueryParams(): DateRangeParams {
    const range = this.appliedRange();
    return {
      from: this.toLocalOffsetIso(range.from, false),
      to: this.toLocalOffsetIso(range.to, true),
    };
  }

  private toLocalOffsetIso(value: string, endOfDay: boolean): string | undefined {
    if (!value) {
      return undefined;
    }

    const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const seconds = `${date.getSeconds()}`.padStart(2, '0');
    const milliseconds = `${date.getMilliseconds()}`.padStart(3, '0');
    const timezoneOffsetMinutes = -date.getTimezoneOffset();
    const sign = timezoneOffsetMinutes >= 0 ? '+' : '-';
    const absoluteOffset = Math.abs(timezoneOffsetMinutes);
    const offsetHours = `${Math.floor(absoluteOffset / 60)}`.padStart(2, '0');
    const offsetMinutes = `${absoluteOffset % 60}`.padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetHours}:${offsetMinutes}`;
  }

  private formatDateInput(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private daysAgo(days: number): Date {
    const value = new Date();
    value.setDate(value.getDate() - days);
    return value;
  }

  private normalizeRange(from: string, to: string): DraftRange {
    if (!from || !to || from <= to) {
      return { from, to };
    }

    return { from: to, to: from };
  }
}
